import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import * as ph from '../services/posthogClient';
import { getFavorites, addFavorite as apiAddFavorite, removeFavorite as apiRemoveFavorite } from '../api/client';
import { useAuth } from './AuthContext';

/**
 * Favorites Context
 *
 * Manages user's favorite ski resorts.
 * Syncs with backend when authenticated.
 */

const FavoritesContext = createContext(undefined);

// Local storage key for guest favorites
const GUEST_FAVORITES_KEY = 'snowfield_guest_favorites';

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Create a Set for O(1) lookups
  const favoriteIds = useMemo(() => {
    return new Set(favorites.map(f => f.locationId));
  }, [favorites]);

  // Load favorites when auth state changes
  useEffect(() => {
    if (authLoading) return;

    if (isAuthenticated) {
      loadFavorites();
    } else {
      // Load from localStorage for guests
      loadGuestFavorites();
    }
  }, [isAuthenticated, authLoading, user?.id]);

  const loadFavorites = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getFavorites();
      setFavorites(data.favorites || []);

      // Merge any guest favorites when logging in
      // SECURITY: Safe JSON parsing with error handling
      let guestFavorites = [];
      try {
        guestFavorites = JSON.parse(localStorage.getItem(GUEST_FAVORITES_KEY) || '[]');
        if (!Array.isArray(guestFavorites)) guestFavorites = [];
      } catch {
        // Ignore malformed localStorage data
        localStorage.removeItem(GUEST_FAVORITES_KEY);
      }
      if (guestFavorites.length > 0) {
        for (const locationId of guestFavorites) {
          if (!data.favorites?.find(f => f.locationId === locationId)) {
            try {
              await apiAddFavorite(locationId);
            } catch (err) {
              console.warn('Failed to migrate guest favorite:', locationId);
            }
          }
        }
        // Clear guest favorites after migration
        localStorage.removeItem(GUEST_FAVORITES_KEY);
        // Reload to get merged list
        const merged = await getFavorites();
        setFavorites(merged.favorites || []);
      }
    } catch (err) {
      console.error('Load favorites error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadGuestFavorites = useCallback(() => {
    try {
      const stored = localStorage.getItem(GUEST_FAVORITES_KEY);
      if (stored) {
        const locationIds = JSON.parse(stored);
        setFavorites(locationIds.map(id => ({ locationId: id })));
      } else {
        setFavorites([]);
      }
    } catch (err) {
      console.error('Load guest favorites error:', err);
      setFavorites([]);
    }
  }, []);

  const saveGuestFavorites = useCallback((newFavorites) => {
    const locationIds = newFavorites.map(f => f.locationId);
    localStorage.setItem(GUEST_FAVORITES_KEY, JSON.stringify(locationIds));
  }, []);

  const addFavorite = useCallback(async (locationId) => {
    ph.capture('favorite_added', { location_id: locationId });
    // Optimistic update
    const newFavorite = {
      locationId,
      createdAt: new Date().toISOString(),
    };
    setFavorites(prev => [...prev, newFavorite]);

    if (isAuthenticated) {
      try {
        await apiAddFavorite(locationId);
      } catch (err) {
        // Revert on error
        setFavorites(prev => prev.filter(f => f.locationId !== locationId));
        setError(err.message);
        throw err;
      }
    } else {
      // Save to localStorage for guests
      saveGuestFavorites([...favorites, newFavorite]);
    }
  }, [isAuthenticated, favorites, saveGuestFavorites]);

  const removeFavorite = useCallback(async (locationId) => {
    ph.capture('favorite_removed', { location_id: locationId });
    // Optimistic update
    const prevFavorites = favorites;
    setFavorites(prev => prev.filter(f => f.locationId !== locationId));

    if (isAuthenticated) {
      try {
        await apiRemoveFavorite(locationId);
      } catch (err) {
        // Revert on error
        setFavorites(prevFavorites);
        setError(err.message);
        throw err;
      }
    } else {
      // Save to localStorage for guests
      saveGuestFavorites(favorites.filter(f => f.locationId !== locationId));
    }
  }, [isAuthenticated, favorites, saveGuestFavorites]);

  const toggleFavorite = useCallback(async (locationId) => {
    if (favoriteIds.has(locationId)) {
      await removeFavorite(locationId);
      return false;
    } else {
      await addFavorite(locationId);
      return true;
    }
  }, [favoriteIds, addFavorite, removeFavorite]);

  const isFavorite = useCallback((locationId) => {
    return favoriteIds.has(locationId);
  }, [favoriteIds]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    favorites,
    favoriteIds,
    isLoading,
    error,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    clearError,
    reload: loadFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

/**
 * Hook to access favorites context
 */
export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

export default FavoritesContext;
